import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Eye, Pencil, Trash2, MessageSquare, Plus, X, CheckCircle, BarChart3, Clock, XCircle, Search, RotateCcw, AlertCircle } from 'lucide-react';

import {
  MessageTemplate,
  NewTemplateData,
  MessageTemplatesProps,
} from '@/types';

import { Twemoji } from 'react-emoji-render';
import { useAuth } from '@/contexts/AuthContext';
import CreateTemplateForm from './CreateTemplateForm';
import PageTutorialButton from '@/components/common/PageTutorialButton';
import {
  getLocalTemplates,
  getMetaTemplatesList,
  getTemplatesInActiveUse,
} from '@/lib/templatesApi';

const DEFAULT_PAGE_SIZE = 6;



const MessageTemplates: React.FC<MessageTemplatesProps> = ({
  onSelectTemplate,
  selectedTemplate,
  mode = 'management'
}) => {
  const { token, logout } = useAuth();

  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [pageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Estados para modales
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name?: string } | null>(null);
  const [checkingUsage, setCheckingUsage] = useState<boolean>(false);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [editingTemplate, setEditingTemplate] = useState<MessageTemplate | null>(null);
  const [originalTemplateName, setOriginalTemplateName] = useState<string>('');

  const [showDetailsDialog, setShowDetailsDialog] = useState<boolean>(false);
  const [selectedTemplateDetails, setSelectedTemplateDetails] = useState<MessageTemplate | null>(null);

  const [showCreateForm, setShowCreateForm] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [newTemplate, setNewTemplate] = useState<NewTemplateData>({
    name: "",
    category: "Utilidad",
    language: "es",
    template: ""
  });

  // Meta templates state
  const [metaTemplates, setMetaTemplates] = useState<any[]>([]);
  const [loadingMetaTemplates, setLoadingMetaTemplates] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  // Estado para plantillas en uso por campañas activas
  const [templatesInActiveUse, setTemplatesInActiveUse] = useState<Set<string>>(new Set());

  const enrichWithMeta = useCallback((mapped: MessageTemplate[], metaList: any[]): MessageTemplate[] => {
    if (!metaList?.length) return mapped;

    const metaMap: Record<string, any> = {};
    metaList.forEach((t: any) => {
      if (!t.name) return;
      const headerComp = t.components?.find((c: any) => c.type === 'HEADER');
      const footerComp = t.components?.find((c: any) => c.type === 'FOOTER');
      const buttonsComp = t.components?.find((c: any) => c.type === 'BUTTONS');

      let headerType: 'TEXT' | 'IMAGE' | null = null;
      let headerText: string | null = null;
      let headerImageUrl: string | null = null;

      if (headerComp) {
        headerType = headerComp.format as 'TEXT' | 'IMAGE';
        if (headerComp.format === 'TEXT') {
          headerText = headerComp.text;
        } else if (headerComp.format === 'IMAGE') {
          headerImageUrl = headerComp.example?.header_handle?.[0] ||
            headerComp.example?.header_url?.[0] ||
            null;
        }
      }

      metaMap[t.name] = {
        header_type: headerType,
        header_text: headerText,
        header_image_url: headerImageUrl,
        footer_text: footerComp?.text || null,
        buttons: buttonsComp?.buttons?.map((btn: any) => ({
          type: btn.type,
          text: btn.text,
          url: btn.url,
          phone_number: btn.phone_number
        })) || []
      };
    });

    return mapped.map(t => {
      const normalizedName = t.name.toLowerCase().replace(/\s+/g, '_');
      const metaData = metaMap[t.name] || metaMap[normalizedName];
      return {
        ...t,
        header_type: metaData?.header_type || (t as any).header_type || null,
        header_text: metaData?.header_text || (t as any).header_text || null,
        header_image_url: metaData?.header_image_url || (t as any).header_image_url || null,
        footer_text: metaData?.footer_text || (t as any).footer_text || null,
        buttons: (metaData?.buttons?.length ? metaData.buttons : (t as any).buttons) || []
      };
    });
  }, []);

  // Fetch templates function (extracted to reuse)
  const fetchTemplates = useCallback(async (force = false) => {
    setLoading(true);
    try {
      if (!token) {
        console.warn('⚠️ No hay token, usando plantillas por defecto');
        setLoading(false);
        return;
      }

      const data = await getLocalTemplates({ force });
      const mapped: MessageTemplate[] = data.map((t: any) => ({
        id: t.id ?? t._id ?? String(Math.random()),
        name: t.name ?? 'Sin nombre',
        category: t.category ?? 'General',
        template: t.template ?? '',
        variables: Array.isArray(t.variables)
          ? t.variables
          : (typeof t.variables === 'string' ? t.variables.split(',').map((s: string) => s.trim()) : []),
        usedCount: Number(t.usedCount ?? 0),
        // ✅ Preserve DB status as fallback when provider API (Gupshup/Meta) doesn't have this template
        meta_status: t.meta_status ?? null,
        // ✅ Componentes visuales del backend (fallback cuando Meta no los tiene)
        header_type: t.header_type ?? null,
        header_text: t.header_text ?? null,
        header_image_url: t.header_image_url ?? null,
        footer_text: t.footer_text ?? null,
        buttons: t.buttons ?? [],
      }));

      // ✅ Deduplicar templates por nombre (normalizado)
      const seenNames = new Set<string>();
      const deduplicated = mapped.filter((template) => {
        // Normalizar nombre: lowercase, reemplazar espacios con guiones bajos
        const normalizedName = template.name.toLowerCase().replace(/\s+/g, '_');
        if (seenNames.has(normalizedName)) {
          console.log(`🔄 Omitiendo template duplicado: "${template.name}"`);
          return false;
        }
        seenNames.add(normalizedName);
        return true;
      });

      if (deduplicated.length > 0) {
        // ✅ Una sola llamada a Meta (compartida / deduplicada) para enriquecer
        try {
          setLoadingMetaTemplates(true);
          const metaRes = await getMetaTemplatesList({ force });
          if (metaRes.success && metaRes.templates?.length) {
            setMetaTemplates(metaRes.templates);
            setTemplates(enrichWithMeta(deduplicated, metaRes.templates));

            const synced = metaRes.synced;
            if (synced?.removed_count && synced.removed_count > 0) {
              toast.info(`Sincronización completada: ${synced.removed_count} plantilla(s) obsoleta(s) eliminada(s)`, {
                duration: 4000
              });
              // Local list may be stale after provider sync — reload once
              const refreshed = await getLocalTemplates({ force: true });
              const remapped: MessageTemplate[] = refreshed.map((t: any) => ({
                id: t.id ?? t._id ?? String(Math.random()),
                name: t.name ?? 'Sin nombre',
                category: t.category ?? 'General',
                template: t.template ?? '',
                variables: Array.isArray(t.variables)
                  ? t.variables
                  : (typeof t.variables === 'string' ? t.variables.split(',').map((s: string) => s.trim()) : []),
                usedCount: Number(t.usedCount ?? 0),
                meta_status: t.meta_status ?? null,
                header_type: t.header_type ?? null,
                header_text: t.header_text ?? null,
                header_image_url: t.header_image_url ?? null,
                footer_text: t.footer_text ?? null,
                buttons: t.buttons ?? [],
              }));
              setTemplates(enrichWithMeta(remapped, metaRes.templates));
            }
          } else {
            setTemplates(deduplicated);
          }
        } catch (metaErr) {
          console.warn('No se pudieron cargar datos de Meta para enriquecer:', metaErr);
          setTemplates(deduplicated);
        } finally {
          setLoadingMetaTemplates(false);
        }
        setCurrentPage(1);
      } else {
        // No hay templates en la base de datos — mostrar estado vacío
        // No hacemos fallback a Gupshup para evitar mostrar plantillas por defecto
        setTemplates([]);
      }
    } catch (err: any) {
      console.error('❌ Error cargando plantillas:', err);
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        toast.error('Sesión expirada');
        await logout();
      } else {
        toast.error('Error al cargar plantillas');
      }
    } finally {
      setLoading(false);
    }
  }, [token, logout, enrichWithMeta]);

  // Función para obtener plantillas que están siendo usadas por campañas activas
  const fetchTemplatesInActiveUse = useCallback(async (force = false) => {
    if (!token) return;

    try {
      const names = await getTemplatesInActiveUse({ force });
      setTemplatesInActiveUse(new Set(names));
      console.log('✅ Templates en uso por campañas activas:', names);
    } catch (error) {
      console.error('Error obteniendo plantillas en uso:', error);
      // En caso de error, mantener el estado vacío (permite eliminar por defecto)
      setTemplatesInActiveUse(new Set());
    }
  }, [token]);

  // Fetch Meta templates with status (refresh / post-mutation). Shares inflight with fetchTemplates.
  const fetchMetaTemplates = useCallback(async (force = false) => {
    if (!token) return;

    setLoadingMetaTemplates(true);
    try {
      const response = await getMetaTemplatesList({ force });
      if (response.success) {
        setMetaTemplates(response.templates || []);
        console.log('✅ Meta templates loaded:', response.templates);

        // Log synchronization info
        const synced = response.synced;
        if (synced && synced.removed_count && synced.removed_count > 0) {
          console.log(`🔄 Sincronización: ${synced.removed_count} plantilla(s) eliminada(s) de la base de datos:`, synced.removed_templates);
          toast.info(`Sincronización completada: ${synced.removed_count} plantilla(s) obsoleta(s) eliminada(s)`, {
            duration: 4000
          });

          // Reload templates from the main collection to reflect deletions
          await fetchTemplates(true);
        }
      }
    } catch (error: any) {
      console.error('Error loading Meta templates:', error);
      // Don't show error toast if no connection - it's optional
      if (error.response?.status !== 400) {
        console.warn('Could not load Meta templates:', error.response?.data?.detail);
      }
    } finally {
      setLoadingMetaTemplates(false);
    }
  }, [token, fetchTemplates]);

  // Cargar plantillas del backend — una sola vez (meta/list ya va dentro de fetchTemplates)
  useEffect(() => {
    if (!token) return;
    void fetchTemplates();
    void fetchTemplatesInActiveUse();
  }, [token, fetchTemplates, fetchTemplatesInActiveUse]);

  // Get Meta template status by name
  const getMetaTemplateStatus = (templateName: string): { status: string; color: string; icon: string; label: string } | null => {
    const metaTemplate = metaTemplates.find(t =>
      t.name?.toLowerCase() === templateName?.toLowerCase().replace(/\s+/g, '_')
    );

    if (!metaTemplate) return null;

    const status = metaTemplate.status || 'UNKNOWN';

    const statusConfig: Record<string, { color: string; icon: string; label: string }> = {
      'APPROVED': { color: 'bg-green-100 text-green-800 border-green-300', icon: '🟢', label: 'Aprobado' },
      'PENDING': { color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: '🟡', label: 'Pendiente' },
      'REJECTED': { color: 'bg-red-100 text-red-800 border-red-300', icon: '🔴', label: 'Rechazado' },
      'DISABLED': { color: 'bg-gray-100 text-gray-800 border-gray-300', icon: '⚪', label: 'Deshabilitado' },
      'PAUSED': { color: 'bg-orange-100 text-orange-800 border-orange-300', icon: '🟠', label: 'Pausado' },
    };

    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-600', icon: '❔', label: status };

    return {
      status,
      ...config
    };
  };

  // Filtrar templates por búsqueda y estado
  const filteredTemplates = useMemo(() => {
    let filtered = templates;

    // Filtrar por búsqueda
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(t =>
        t.name.toLowerCase().includes(searchLower) ||
        t.template.toLowerCase().includes(searchLower)
      );
    }

    // Filtrar por estado de Meta
    if (statusFilter && statusFilter !== 'ALL') {
      filtered = filtered.filter(t => {
        const metaStatus = getMetaTemplateStatus(t.name);
        return metaStatus?.status === statusFilter;
      });
    }

    return filtered;
  }, [templates, search, statusFilter, metaTemplates]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(filteredTemplates.length / pageSize)), [filteredTemplates, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const visibleTemplates = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredTemplates.slice(start, start + pageSize);
  }, [filteredTemplates, currentPage, pageSize]);

  const extractVariables = (template: string): string[] => {
    const regex = /\{\{([^}]+)\}\}/g;
    const variables: string[] = [];
    let match;

    while ((match = regex.exec(template)) !== null) {
      const varName = match[1].trim();
      if (!variables.includes(varName)) {
        variables.push(varName);
      }
    }

    return variables;
  };

  const handleCreateTemplate = async (payloadFromForm?: any): Promise<void> => {
    // If _skipInsert is true, the template was already created by /whatsapp/templates/create
    // We just need to refresh the list and close the form
    if (payloadFromForm?._skipInsert) {
      await fetchTemplates(true);
      setNewTemplate({ name: '', category: 'Utilidad', language: 'es', template: '' });
      setShowCreateForm(false);
      return;
    }

    if (!newTemplate.name || !newTemplate.template) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    const variables = extractVariables(newTemplate.template);

    try {
      if (!token) {
        toast.error('No estás autenticado');
        return;
      }

      const payload = {
        name: newTemplate.name,
        category: newTemplate.category || 'General',
        template: newTemplate.template,
        variables,
        language: newTemplate.language || 'es'
      };

      const endpoint = (axios.defaults.baseURL || '').endsWith('/api') ? '/templates/' : '/api/templates/';
      const res = await axios.post(endpoint, payload);

      const created = res.data;
      const tpl: MessageTemplate = {
        id: created.id ?? created._id ?? String(Math.random()),
        name: created.name ?? newTemplate.name,
        category: created.category ?? newTemplate.category ?? 'General',
        template: created.template ?? newTemplate.template,
        variables: Array.isArray(created.variables) ? created.variables : variables,
        usedCount: Number(created.usedCount ?? 0)
      };

      setTemplates(prev => [tpl, ...prev]);
      setNewTemplate({ name: '', category: 'Utilidad', language: 'es', template: '' });
      setShowCreateForm(false);
      setCurrentPage(1);

      toast.success('¡Plantilla creada!');
    } catch (err: any) {
      console.error('❌ Error creating template:', err);
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        toast.error('Sesión expirada');
        await logout();
      } else {
        toast.error('Error al crear plantilla');
      }
    }
  };

  const openDeleteConfirm = async (template: MessageTemplate) => {
    setDeleteTarget({ id: template.id, name: template.name });
    setUsageError(null);
    setCheckingUsage(true);
    setShowDeleteModal(true);

    try {
      // Verificar si la plantilla está en uso
      const endpoint = (axios.defaults.baseURL || '').endsWith('/api')
        ? `/templates/check-usage/${encodeURIComponent(template.name)}`
        : `/api/templates/check-usage/${encodeURIComponent(template.name)}`;

      const response = await axios.get(endpoint);

      if (response.data.in_use) {
        // Mostrar mensaje con el nombre de la campaña y su ESTADO
        const campaign = response.data.campaigns[0];

        // Traducir estado para mostrarlo amigablemente
        let statusText = campaign.status;
        if (campaign.status === 'active') statusText = 'Activa (Aprobada)';
        else if (campaign.status === 'pending_template_approval') statusText = 'Pendiente de aprobación';
        else if (campaign.status === 'running') statusText = 'En ejecución';
        else if (campaign.status === 'scheduled') statusText = 'Programada';
        else if (campaign.status === 'pending') statusText = 'Pendiente';

        setUsageError(
          `Esta plantilla no se puede eliminar porque está siendo utilizada en la campaña "${campaign.campaign_name}" (${campaign.campaign_type}).\nEstado actual: ${statusText}`
        );
      }
    } catch (err: any) {
      console.error('Error verificando uso de plantilla:', err);
      // Si hay error, permitir eliminar (fail-open)
    } finally {
      setCheckingUsage(false);
    }
  };

  const closeDeleteConfirm = () => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
    setUsageError(null);
  };

  const openEditModal = (template: MessageTemplate) => {
    setEditingTemplate(template);
    setOriginalTemplateName(template.name); // ✅ Guardar nombre original
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingTemplate(null);
    setOriginalTemplateName(''); // ✅ Limpiar
  };

  const handleDeleteTemplate = async (id: string): Promise<void> => {
    try {
      if (!token) {
        toast.error('No estás autenticado');
        return;
      }

      const endpoint = (axios.defaults.baseURL || '').endsWith('/api') ? `/templates/${id}` : `/api/templates/${id}`;
      const response = await axios.delete(endpoint);

      setTemplates(prev => prev.filter(t => t.id !== id));

      // Check if Meta deletion was attempted and succeeded
      const metaDeletion = response.data?.meta_deletion;
      if (metaDeletion?.attempted) {
        if (metaDeletion.success) {
          toast.success('Plantilla eliminada de la base de datos y Meta');
        } else {
          toast.warning('Plantilla eliminada de la base de datos, pero no se pudo eliminar de Meta');
        }
      } else {
        toast.success('Plantilla eliminada');
      }

      // Refresh local + Meta templates to reflect changes
      await fetchTemplates(true);
    } catch (err: any) {
      console.error('❌ Error eliminando plantilla:', err);
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        toast.error('Sesión expirada');
        await logout();
      } else {
        toast.error('Error al eliminar plantilla');
      }
    } finally {
      closeDeleteConfirm();
    }
  };

  const handleUpdateTemplate = async (payload: NewTemplateData): Promise<void> => {
    if (!editingTemplate) return;

    try {
      if (!token) {
        toast.error('No estás autenticado');
        return;
      }

      const endpoint = (axios.defaults.baseURL || '').endsWith('/api')
        ? `/templates/${editingTemplate.id}`
        : `/api/templates/${editingTemplate.id}`;

      const updatePayload = {
        name: payload.name,
        category: payload.category || 'General',
        template: payload.template,
        variables: extractVariables(payload.template),
        language: payload.language || 'es'
      };

      const res = await axios.patch(endpoint, updatePayload);

      const updated = res.data;
      const updatedTemplate: MessageTemplate = {
        id: updated.id ?? editingTemplate.id,
        name: updated.name ?? payload.name,
        category: updated.category ?? payload.category ?? 'General',
        template: updated.template ?? payload.template,
        variables: Array.isArray(updated.variables)
          ? updated.variables
          : extractVariables(payload.template),
        usedCount: Number(updated.usedCount ?? editingTemplate.usedCount ?? 0)
      };

      setTemplates(prev =>
        prev.map(t => t.id === editingTemplate.id ? updatedTemplate : t)
      );

      toast.success('¡Plantilla actualizada!');
      closeEditModal();
    } catch (err: any) {
      console.error('❌ Error actualizando plantilla:', err);
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        toast.error('Sesión expirada');
        await logout();
      } else {
        toast.error('Error al actualizar plantilla');
      }
    }
  };

  const getCategoryColor = (category: string): string => {
    const colors: Record<string, string> = {
      'Facturas': 'bg-blue-100 text-blue-700',
      'Confirmaciones': 'bg-green-100 text-green-700',
      'Recordatorios': 'bg-yellow-100 text-yellow-700',
      'Marketing': 'bg-purple-100 text-purple-700',
      'General': 'bg-gray-100 text-gray-700'
    };
    return colors[category] || colors['General'];
  };

  const handleShowDetails = (template: MessageTemplate) => {
    setSelectedTemplateDetails(template);
    setShowDetailsDialog(true);
  };

  // Formatear nombre: reemplazar guiones bajos por espacios y capitalizar
  const formatTemplateName = (name: string): string => {
    if (!name) return 'Sin nombre';
    return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const goToPage = (page: number) => {
    if (page < 1) page = 1;
    if (page > totalPages) page = totalPages;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const TemplateCard: React.FC<{ template: MessageTemplate }> = ({ template }) => {
    const metaStatus = getMetaTemplateStatus(template.name);
    const btnBase = "h-10 px-3 flex items-center justify-center rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 border";

    // Configuración de estados
    const statusConfig: Record<string, { color: string; label: string }> = {
      'APPROVED': { color: 'bg-green-100 text-green-800 border-green-300', label: 'Aprobado' },
      'PENDING': { color: 'bg-yellow-100 text-yellow-800 border-yellow-300', label: 'Pendiente' },
      'REJECTED': { color: 'bg-red-100 text-red-800 border-red-300', label: 'Rechazado' },
      'DISABLED': { color: 'bg-gray-100 text-gray-800 border-gray-300', label: 'Deshabilitado' },
      'PAUSED': { color: 'bg-orange-100 text-orange-800 border-orange-300', label: 'Pausado' },
    };

    // Priority: 1) Live status from provider API  2) Status saved in DB  3) Default PENDING
    // This handles templates created with the old Meta connection that don't exist in Gupshup yet.
    const currentStatus = metaStatus?.status || (template as any).meta_status || 'PENDING';
    const statusStyle = statusConfig[currentStatus] || statusConfig['PENDING'];

    // Verificar si la plantilla está en uso por alguna campaña activa
    const normalizedTemplateName = template.name.toLowerCase().replace(/\s+/g, '_');
    const isInActiveUse = templatesInActiveUse.has(normalizedTemplateName);

    // Determinar si se puede eliminar la plantilla:
    // 1. PENDING: NO se puede eliminar (esperando aprobación de Meta)
    // 2. REJECTED: SÍ se puede eliminar (plantilla rechazada por Meta)
    // 3. APPROVED + en uso por campaña ACTIVA: NO se puede eliminar
    // 4. APPROVED + NO en uso (o solo campañas completadas): SÍ se puede eliminar
    // 5. DISABLED: SÍ se puede eliminar
    // 6. Sin estado de Meta (null): NO se puede eliminar por defecto (está pendiente)
    let canDelete = false;
    let deleteBlockReason = '';

    if (currentStatus === 'PENDING') {
      canDelete = false;
      deleteBlockReason = 'Pendiente de aprobación';
    } else if (currentStatus === 'REJECTED' || currentStatus === 'DISABLED') {
      canDelete = true;
    } else if (currentStatus === 'APPROVED') {
      if (isInActiveUse) {
        canDelete = false;
        deleteBlockReason = 'En uso por campaña activa';
      } else {
        canDelete = true;
      }
    } else if (currentStatus === 'PAUSED') {
      if (isInActiveUse) {
        canDelete = false;
        deleteBlockReason = 'En uso por campaña activa';
      } else {
        canDelete = true;
      }
    } else {
      // Sin estado conocido, asumir pendiente
      canDelete = false;
      deleteBlockReason = 'Pendiente de aprobación';
    }

    return (
      <Card className="relative bg-white hover:shadow-2xl transition-all duration-300 rounded-xl border border-gray-200/60 shadow-md h-full flex flex-col">
        <CardContent className="p-6 flex flex-col h-full">
          {/* Header - igual que campañas */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-[#E8F7F7] border border-[#1CB3B3]/30 rounded-xl flex items-center justify-center">
                <MessageSquare className="h-5 w-5 text-[#0D7377]" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{formatTemplateName(template.name)}</h3>
                <p className="text-sm text-gray-500">
                  Usado {template.usedCount} veces
                </p>
              </div>
            </div>
            {/* Estado a la derecha - siempre visible */}
            <div>
              <Badge
                variant="secondary"
                className={`${statusStyle.color} border px-3 py-1 rounded-md`}
              >
                {statusStyle.label}
              </Badge>
            </div>
          </div>

          {/* Sección Plantilla - con scroll para contenido largo */}
          <div className="mb-4 p-3 bg-[#FAFBFC] border border-gray-200 rounded-xl flex-grow max-h-56 overflow-y-auto">
            <div className="text-xs font-medium text-gray-500 uppercase pb-1">Plantilla</div>

            {/* Header - imagen o texto */}
            {template.header_type === 'IMAGE' && template.header_image_url ? (
              <img
                src={template.header_image_url}
                alt="Header de plantilla"
                className="w-full h-24 object-cover rounded-lg mt-1"
              />
            ) : template.header_text ? (
              <div className="text-sm font-semibold text-gray-800 mt-1 whitespace-pre-wrap">
                {template.header_text}
              </div>
            ) : null}

            {/* Body - texto principal con preservación de espacios */}
            <div className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">
              {template.template}
            </div>

            {/* Footer - si existe */}
            {template.footer_text && (
              <div className="text-xs text-gray-500 mt-2 italic whitespace-pre-wrap">
                {template.footer_text}
              </div>
            )}

            {/* Botones - si existen (centrados) */}
            {template.buttons && template.buttons.length > 0 && (
              <div className="flex flex-wrap justify-center gap-1.5 mt-2 pt-2 border-t border-gray-200">
                {template.buttons.map((btn, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center px-2 py-1 bg-[#E8F7F7] text-[#0D7377] rounded text-xs border border-[#1CB3B3]/30"
                  >
                    {btn.text}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Botones - solo mostrar eliminar si el estado permite eliminación */}
          <div className="flex justify-center mt-auto">
            {mode === 'selection' && onSelectTemplate ? (
              <Button
                size="sm"
                className={`${btnBase} bg-[#E8F7F7] text-[#0D7377] border-[#1CB3B3]/30 hover:bg-[#D5F0F0] hover:border-[#1CB3B3]/50 shadow-sm w-full`}
                onClick={() => onSelectTemplate(template)}
              >
                <CheckCircle className="h-4 w-4 mr-1.5" /> Seleccionar
              </Button>
            ) : canDelete ? (
              <Button
                size="sm"
                className={`${btnBase} bg-rose-50 text-rose-600 border-rose-200 hover:bg-rose-100 hover:border-rose-300 shadow-sm w-full`}
                onClick={() => openDeleteConfirm(template)}
              >
                <Trash2 className="h-4 w-4 mr-1.5" /> Eliminar
              </Button>
            ) : (
              <div className="text-xs text-gray-400 italic text-center py-2">
                {deleteBlockReason || 'No se puede eliminar'}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  // Skeleton loader para plantillas
  if (loading) {
    return (
      <div className="max-w-[1600px] mx-auto">
        <div className="space-y-6 animate-fadeIn">
          {/* Header skeleton */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div>
              <div className="h-8 w-56 bg-gray-200 rounded-lg animate-pulse"></div>
              <div className="h-4 w-64 bg-gray-100 rounded mt-2 animate-pulse"></div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-gray-200 rounded-xl animate-pulse"></div>
              <div className="w-40 h-11 bg-gray-200 rounded-xl animate-pulse"></div>
            </div>
          </div>

          {/* Search bar skeleton */}
          <div className="bg-white border border-gray-200 rounded-xl px-4 py-6">
            <div className="h-12 bg-gray-100 rounded-md animate-pulse"></div>
          </div>

          {/* Stats cards skeleton */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="h-4 w-20 bg-gray-200 rounded"></div>
                      <div className="h-8 w-12 bg-gray-100 rounded mt-2"></div>
                    </div>
                    <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Template cards skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="h-full animate-pulse">
                <CardContent className="p-6">
                  {/* Header skeleton */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-xl"></div>
                      <div>
                        <div className="h-5 w-36 bg-gray-200 rounded"></div>
                        <div className="h-3 w-20 bg-gray-100 rounded mt-1"></div>
                      </div>
                    </div>
                    <div className="h-6 w-20 bg-gray-200 rounded-md"></div>
                  </div>

                  {/* Template preview skeleton */}
                  <div className="mb-4 p-3 bg-[#FAFBFC] border border-gray-200 rounded-xl">
                    <div className="h-3 w-16 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 w-full bg-gray-100 rounded"></div>
                    <div className="h-3 w-3/4 bg-gray-100 rounded mt-1"></div>
                  </div>

                  {/* Button skeleton */}
                  <div className="h-10 bg-gray-100 rounded-lg"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] mx-auto">
      <div className="space-y-6 animate-fadeIn">
        {/* Header - oculto mientras se está creando una plantilla */}
        {!showCreateForm && (
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between space-y-4 xl:space-y-0">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Plantillas de Mensajes</h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1 leading-relaxed">
                Crea y gestiona plantillas reutilizables
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-start gap-2 w-full xl:flex-nowrap xl:w-auto xl:justify-end">
              <PageTutorialButton tutorialKey="templates" />
              {mode === 'management' && (
                <>
                  <Button
                    onClick={async () => {
                      await Promise.all([
                        fetchTemplates(true),
                        fetchTemplatesInActiveUse(true),
                      ]);
                      toast.success("Plantillas actualizadas");
                    }}
                    disabled={loading || loadingMetaTemplates}
                    variant="outline"
                    className="bg-white hover:bg-slate-50 border-gray-200 text-gray-500 w-11 h-11 p-0 rounded-xl shadow-sm transition-all active:scale-95 group"
                  >
                    <RotateCcw className={`w-5 h-5 transition-transform duration-500 ${loading || loadingMetaTemplates ? 'animate-spin' : 'group-hover:rotate-180'}`} />
                  </Button>

                  <Button
                    onClick={() => {
                      setNewTemplate({
                        name: "",
                        category: "Utilidad",
                        language: "es",
                        template: "",
                      });
                      setShowCreateForm(true);
                    }}
                    className="w-full sm:w-auto bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] hover:from-[#1a3558] hover:to-[#189999] text-white px-4 sm:px-6 h-11 rounded-xl shadow-lg flex items-center justify-center gap-2 font-semibold transition-all active:scale-95"
                  >
                    <Plus className="h-5 w-5" />
                    Crea una nueva plantilla
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Mostrar formulario de creación cuando el usuario lo solicite */}
        {showCreateForm && mode === 'management' && (
          <div className="mt-6">
            <CreateTemplateForm
              newTemplate={newTemplate}
              setNewTemplate={setNewTemplate}
              extractVariables={extractVariables}
              handleCreateTemplate={handleCreateTemplate}
              setShowCreateForm={setShowCreateForm}
            />
          </div>
        )}

        {/* Si el formulario de creación está abierto, ocultamos el resto de la UI */}
        {showCreateForm ? null : (
          <>
            {/* Search / Filters - encima de las stats cards como en campañas */}
            <div className="mb-6">
              <div className="bg-white border border-gray-200 rounded-xl px-4 py-6">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    className="w-full pl-14 pr-4 py-3 rounded-md text-sm bg-white border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#1CB3B3] focus:border-[#1CB3B3] transition-colors"
                    placeholder="Buscar por nombre o texto de plantilla..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Stats Cards */}
            {mode === 'management' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card className={`cursor-pointer hover:shadow-lg transition ${statusFilter === 'ALL' ? 'ring-2 ring-[#e6f7f4]' : ''}`} onClick={() => setStatusFilter('ALL')}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Total</p>
                        <p className="text-2xl font-bold text-[#1fb6b4]">{templates.length}</p>
                      </div>
                      <div className="w-8 h-8 bg-[#E8F7F7] rounded-lg flex items-center justify-center">
                        <BarChart3 className="h-4 w-4 text-[#1CB3B3]" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className={`cursor-pointer hover:shadow-lg transition ${statusFilter === 'APPROVED' ? 'ring-2 ring-green-200' : ''}`} onClick={() => setStatusFilter('APPROVED')}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Aprobadas</p>
                        <p className="text-2xl font-bold text-green-600">
                          {metaTemplates.filter(t => t.status === 'APPROVED').length}
                        </p>
                      </div>
                      <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className={`cursor-pointer hover:shadow-lg transition ${statusFilter === 'PENDING' ? 'ring-2 ring-yellow-200' : ''}`} onClick={() => setStatusFilter('PENDING')}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Pendientes</p>
                        <p className="text-2xl font-bold text-yellow-600">
                          {metaTemplates.filter(t => t.status === 'PENDING').length}
                        </p>
                      </div>
                      <div className="w-8 h-8 bg-yellow-50 rounded-lg flex items-center justify-center">
                        <Clock className="h-4 w-4 text-yellow-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className={`cursor-pointer hover:shadow-lg transition ${statusFilter === 'REJECTED' ? 'ring-2 ring-red-200' : ''}`} onClick={() => setStatusFilter('REJECTED')}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Rechazadas</p>
                        <p className="text-2xl font-bold text-red-600">
                          {metaTemplates.filter(t => t.status === 'REJECTED').length}
                        </p>
                      </div>
                      <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center">
                        <XCircle className="h-4 w-4 text-red-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Templates List */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  {mode === 'selection' ? 'Selecciona una Plantilla' : 'Plantillas Existentes'}
                </h2>
              </div>

              {filteredTemplates.length === 0 && !loading ? (
                <Card className="border-2 border-dashed border-gray-200">
                  <CardContent className="py-12 text-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] rounded-full flex items-center justify-center mx-auto mb-4">
                      <MessageSquare className="h-7 w-7 text-white" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {search ? "No se encontraron plantillas" : "No hay plantillas aún"}
                    </h3>
                    <p className="text-gray-500 mb-6">
                      {search ? "Ajusta tu búsqueda o los filtros." : "Crea tu primera plantilla para reutilizarla en múltiples campañas."}
                    </p>
                    <div className="flex justify-center gap-2">
                      {search && (
                        <Button variant="outline" onClick={() => { setSearch(""); setStatusFilter("ALL"); setCurrentPage(1); }} className="gap-1.5">
                          <RotateCcw className="h-4 w-4" /> Limpiar filtros
                        </Button>
                      )}
                      {mode === 'management' && !search && (
                        <Button
                          onClick={() => {
                            setNewTemplate({
                              name: "",
                              category: "Utilidad",
                              language: "es",
                              template: "",
                            });
                            setShowCreateForm(true);
                          }}
                          className="bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] text-white"
                        >
                          <Plus className="h-4 w-4 mr-1.5" /> Crear nueva plantilla
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {visibleTemplates.map((template) => (
                      <TemplateCard key={template.id} template={template} />
                    ))}
                  </div>

                  <div className="flex items-center justify-between mt-6">
                    <div className="text-sm text-gray-600">
                      Página {currentPage} de {totalPages} — mostrando {visibleTemplates.length} de {filteredTemplates.length}
                    </div>

                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
                        Anterior
                      </Button>

                      <div className="hidden sm:flex items-center gap-1">
                        {Array.from({ length: totalPages }).map((_, i) => {
                          const page = i + 1;
                          const isActive = page === currentPage;
                          // Mostrar máximo páginas inteligentemente con "..."
                          if (totalPages <= 7 || page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                            return (
                              <button
                                key={page}
                                onClick={() => goToPage(page)}
                                className={`px-3 py-1 rounded-md text-sm transition-colors ${isActive ? 'bg-[#1CB3B3] text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                              >
                                {page}
                              </button>
                            );
                          } else if (page === currentPage - 2 || page === currentPage + 2) {
                            return <span key={page} className="px-1 text-gray-400">...</span>;
                          }
                          return null;
                        })}
                      </div>

                      <Button size="sm" variant="outline" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>
                        Siguiente
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Template Examples - Dentro del condicional */}
            {mode === 'management' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <span>💡</span>
                    <span>Ejemplos de Plantillas</span>
                  </CardTitle>
                  <CardDescription>
                    Ideas para diferentes tipos de mensajes
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium mb-2 text-blue-700">📧 Recordatorio de Factura</h4>
                      <div className="bg-blue-50 p-3 rounded-lg text-sm font-mono">
                        Hola {`{{Nombre}}`}, tu factura {`{{Factura}}`} por {`{{Monto}}`} vence el {`{{Fecha}}`}. ¡No olvides pagarla!
                      </div>
                    </div>

                    <div>
                      <h4 className="font-medium mb-2 text-green-700">✅ Confirmación de Pago</h4>
                      <div className="bg-green-50 p-3 rounded-lg text-sm font-mono">
                        ¡Gracias {`{{Nombre}}`}! Recibimos tu pago de {`{{Monto}}`} para la factura {`{{Factura}}`}. Todo está al día.
                      </div>
                    </div>

                    <div>
                      <h4 className="font-medium mb-2 text-yellow-700">⚠️ Factura Vencida</h4>
                      <div className="bg-yellow-50 p-3 rounded-lg text-sm font-mono">
                        {`Hola {{Nombre}}`}, tu factura {`{{Factura}}`} venció el {`{{Fecha}}`}. Por favor, realiza el pago de {`{{Monto}}`} lo antes posible.
                      </div>
                    </div>

                    <div>
                      <h4 className="font-medium mb-2 text-purple-700">🎉 Bienvenida</h4>
                      <div className="bg-purple-50 p-3 rounded-lg text-sm font-mono">
                        ¡Bienvenido {`{{Nombre}}`}! Tu cuenta ha sido activada. Tu ID de cliente es {`{{ID}}`}. ¡Gracias por confiar en nosotros!
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Modal de Edición */}
        {showEditModal && editingTemplate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-6">
            <div
              className="bg-white rounded-xl shadow-2xl w-full mx-4 overflow-hidden"
              style={{ maxWidth: '95vw', maxHeight: '90vh' }}
            >
              <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-50 to-purple-50">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">✏️ Editar Plantilla</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Modifica los detalles de: <span className="font-semibold">{originalTemplateName}</span>
                  </p>
                </div>
                <Button
                  variant="ghost"
                  onClick={closeEditModal}
                  className="text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                >
                  ✖️
                </Button>
              </div>

              <div
                className="overflow-y-auto p-6"
                style={{ maxHeight: 'calc(90vh - 140px)' }}
              >
                <CreateTemplateForm
                  newTemplate={{
                    name: editingTemplate.name,
                    category: editingTemplate.category,
                    language: 'es',
                    template: editingTemplate.template
                  }}
                  setNewTemplate={(updater) => {
                    if (typeof updater === 'function') {
                      setEditingTemplate(prev => {
                        if (!prev) return null;
                        const currentState = {
                          name: prev.name,
                          category: prev.category,
                          language: 'es',
                          template: prev.template
                        };
                        const newState = updater(currentState);
                        return {
                          ...prev,
                          name: newState.name,
                          category: newState.category,
                          template: newState.template
                        };
                      });
                    } else {
                      setEditingTemplate(prev => {
                        if (!prev) return null;
                        return {
                          ...prev,
                          name: updater.name,
                          category: updater.category,
                          template: updater.template
                        };
                      });
                    }
                  }}
                  extractVariables={extractVariables}
                  handleCreateTemplate={handleUpdateTemplate}
                  setShowCreateForm={closeEditModal}
                  isEditMode={true}
                />
              </div>
            </div>
          </div>
        )}

        {/* Modal de Confirmación de Eliminación */}
        {showDeleteModal && deleteTarget && ReactDOM.createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-40 px-4">
            <div className="bg-white rounded-xl shadow-xl max-w-sm w-full overflow-hidden">
              <div className="p-5">
                <div className="flex items-start gap-4">
                  <div className={`flex-shrink-0 w-12 h-12 rounded-xl ${usageError ? 'bg-orange-50 border border-orange-200' : 'bg-rose-50 border border-rose-200'} flex items-center justify-center`}>
                    {usageError ? (
                      <AlertCircle className="h-6 w-6 text-orange-500" />
                    ) : (
                      <Trash2 className="h-6 w-6 text-rose-500" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {usageError ? 'No se puede eliminar' : 'Eliminar plantilla'}
                    </h3>
                    {checkingUsage ? (
                      <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                        <div className="w-4 h-4 border-2 border-[#1CB3B3] border-t-transparent rounded-full animate-spin" />
                        Verificando uso de la plantilla...
                      </div>
                    ) : usageError ? (
                      <p className="mt-2 text-sm text-orange-700 bg-orange-50 p-3 rounded-lg border border-orange-200">
                        {usageError}
                      </p>
                    ) : (
                      <p className="mt-2 text-sm text-gray-600">
                        ¿Estás seguro de que deseas eliminar la plantilla{' '}
                        <span className="font-semibold text-gray-900">"{deleteTarget.name}"</span>? Esta acción no se puede deshacer.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="px-5 pb-5">
                <div className="flex justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={closeDeleteConfirm}
                    className="px-5 h-10 rounded-lg border-gray-200 hover:bg-gray-50"
                  >
                    {usageError ? 'Cerrar' : 'Cancelar'}
                  </Button>
                  {!usageError && !checkingUsage && (
                    <Button
                      className="px-5 h-10 rounded-lg bg-rose-600 hover:bg-rose-700 text-white font-medium shadow-sm transition-all"
                      onClick={() => handleDeleteTemplate(deleteTarget.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Eliminar
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Modal de Detalles - Mismo estilo que CampaignList */}
        {showDetailsDialog && selectedTemplateDetails && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-6">
            <div
              className="bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 overflow-hidden"
              style={{ maxHeight: '80vh' }}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Detalles de la Plantilla</h2>
                  <p className="text-sm text-gray-500">Información completa y vista previa</p>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    onClick={() => setShowDetailsDialog(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ✖️
                  </Button>
                </div>
              </div>

              {/* Content con scroll interno */}
              <div
                className="p-6 space-y-6 overflow-y-auto"
                style={{ maxHeight: 'calc(80vh - 96px)' }}
              >
                {/* Top: Template summary */}
                <div className="bg-white border rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <div className="text-xs text-gray-400 uppercase tracking-wide">Nombre de la plantilla</div>
                      <div className="mt-1 text-2xl font-bold text-gray-900">{selectedTemplateDetails.name}</div>

                      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div>
                          <div className="text-xs text-gray-400">Categoría</div>
                          <div className="mt-1">
                            <Badge variant="outline" className={getCategoryColor(selectedTemplateDetails.category)}>
                              {selectedTemplateDetails.category}
                            </Badge>
                          </div>
                        </div>

                        <div>
                          <div className="text-xs text-gray-400">Veces usado</div>
                          <div className="text-sm font-medium text-green-600">{selectedTemplateDetails.usedCount} veces</div>
                        </div>

                        <div className="sm:col-span-2">
                          <div className="text-xs text-gray-400">Datos detectados</div>
                          <div className="text-sm font-medium text-gray-800">{selectedTemplateDetails.variables.length} variable(s)</div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col items-end justify-start">
                      <div className="text-xs text-gray-400 uppercase tracking-wide">Estado</div>
                      <div className="mt-2">
                        <Badge variant="outline" className="bg-green-100 text-green-700">
                          Activa
                        </Badge>
                      </div>
                      <div className="mt-4 flex flex-col items-end gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setShowDetailsDialog(false);
                            openEditModal(selectedTemplateDetails);
                          }}
                          className="w-full"
                        >
                          ✏️ Editar
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="h-px bg-gray-100" />

                {/* Bottom: Template content */}
                <div className="bg-white border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="w-full">
                      <div className="text-xs text-gray-400 uppercase tracking-wide">Contenido de la plantilla</div>

                      <div className="mt-4">
                        <div className="text-xs text-gray-400 uppercase tracking-wide mb-2">Mensaje</div>
                        <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 text-sm text-gray-800 whitespace-pre-wrap overflow-y-auto max-h-48 sm:max-h-60">
                          {selectedTemplateDetails.template}
                        </div>
                      </div>

                      {/* Variables section */}
                      {selectedTemplateDetails.variables.length > 0 && (
                        <div className="mt-4">
                          <div className="text-xs text-gray-400 uppercase tracking-wide mb-2">Variables disponibles</div>
                          <div className="flex flex-wrap gap-2">
                            {selectedTemplateDetails.variables.map((variable, index) => (
                              <Badge
                                key={index}
                                variant="secondary"
                                className="bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1"
                              >
                                <span className="font-mono">{`{{${variable}}}`}</span>
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Vista previa section */}
                      <div className="mt-4">
                        <div className="text-xs text-gray-400 uppercase tracking-wide mb-2">Vista previa con datos de ejemplo</div>
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3 text-sm text-gray-800 whitespace-pre-wrap">
                          {(() => {
                            let preview = selectedTemplateDetails.template;
                            const exampleData: Record<string, string> = {
                              'Nombre': 'Juan Pérez',
                              'Factura': 'FAC-2024-001',
                              'Monto': '$1,500.00',
                              'Fecha': '15/01/2024',
                              'ID': 'CLI-12345',
                              'Descuento': '20%',
                              'Producto': 'Laptop Dell',
                              'Empresa': 'Tech Solutions'
                            };

                            selectedTemplateDetails.variables.forEach(variable => {
                              const value = exampleData[variable] || `[${variable}]`;
                              preview = preview.replace(
                                new RegExp(`\\{\\{\\s*${variable}\\s*\\}\\}`, 'g'),
                                value
                              );
                            });

                            return (
                              <div className="[&_img]:inline [&_img]:w-5 [&_img]:h-5 [&_img]:align-text-bottom [&_img]:mx-[1px]">
                                <Twemoji text={preview} svg />
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageTemplates;
